/**
 * De quién son los borradores que quedaron en este teléfono.
 *
 * POR QUÉ EXISTE. Hasta hoy, CUALQUIER `SIGNED_OUT` purgaba los borradores y
 * borraba los bytes del disco. Eso mata el caso que los borradores existen para
 * cubrir: se te vence la sesión a media entrega —parado junto al carro, con
 * catorce fotos tomadas— y al volver a entrar no queda nada. `use-auth.ts`
 * llama a la purga en la rama `SIGNED_OUT` completa, sin distinguir; la prueba
 * de que la rama involuntaria existe está tres líneas abajo, donde pone
 * `setSessionExpired(true)`.
 *
 * La corrección es preservar el borrador cuando la sesión se cae sola. Pero eso
 * abre una pregunta que antes no existía: **si los bytes sobreviven al cierre,
 * ¿qué impide que los vea otra persona?**
 *
 * ESTE ARCHIVO ES LA RESPUESTA. Se guarda quién es el dueño de lo que quedó en
 * disco, y en CADA inicio de sesión se compara. Si entra alguien distinto, se
 * purga antes de que pueda ver nada. Si vuelve el mismo, encuentra su trabajo
 * intacto.
 *
 * POR QUÉ NO SE DEDUCE DEL EVENTO DE AUTENTICACIÓN. `lastAuthenticatedUserId`
 * vive en memoria del módulo: se pierde cuando el proceso muere. Y ese es
 * justamente el camino peligroso —sesión vencida, app cerrada, otra persona
 * entra— donde deducirlo daría "primer inicio de sesión" y no purgaría nada.
 * Un marcador en disco es la única cosa que sobrevive a lo mismo que sobreviven
 * los bytes que protege.
 *
 * NO ES ESTADO DE CUENTA, así que no vive en `lib/stores/` ni pasa por
 * `resetClientState`: es un dato SOBRE los bytes guardados, y tiene que
 * sobrevivir exactamente lo mismo que ellos.
 */
import { Platform } from 'react-native'

const CLAVE = 'tago.drafts.owner.v1'

type MMKVLike = {
  set: (k: string, v: string) => void
  getString: (k: string) => string | undefined
  delete: (k: string) => void
}

let mmkv: MMKVLike | null = null

function almacen(): MMKVLike {
  if (mmkv) return mmkv
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { MMKV } = require('react-native-mmkv') as typeof import('react-native-mmkv')
    mmkv = new MMKV({ id: 'tago-zustand' })
  } catch {
    mmkv = { set: () => {}, getString: () => undefined, delete: () => {} }
  }
  return mmkv
}

function web(): Storage | null {
  if (typeof window === 'undefined' || !window.localStorage) return null
  return window.localStorage
}

/** Quién dejó borradores en este dispositivo, o null si no hay marca. */
export function duenoDeBorradores(): string | null {
  try {
    if (Platform.OS === 'web') return web()?.getItem(CLAVE) ?? null
    return almacen().getString(CLAVE) ?? null
  } catch {
    return null
  }
}

/** Marcar a quién pertenece lo que se está guardando. */
export function marcarDuenoDeBorradores(userId: string): void {
  try {
    if (Platform.OS === 'web') web()?.setItem(CLAVE, userId)
    else almacen().set(CLAVE, userId)
  } catch {
    // La marca es de mejor esfuerzo. Si no se puede escribir, el peor caso es
    // que la próxima sesión purgue de más — nunca que muestre datos ajenos,
    // porque `hayQuePurgarBorradoresDe` trata "sin marca" como desconocido.
  }
}

/** Olvidar la marca. Se llama junto con la purga. */
export function olvidarDuenoDeBorradores(): void {
  try {
    if (Platform.OS === 'web') web()?.removeItem(CLAVE)
    else almacen().delete(CLAVE)
  } catch {
    // ídem
  }
}

/**
 * ¿Los borradores que hay en disco son de OTRA persona?
 *
 * SIN MARCA SE ADOPTA, NO SE PURGA. Mi primera versión hacía lo contrario
 * "por seguridad", y eso convertía este archivo en una bomba de un solo
 * disparo: la marca se escribe DENTRO de `conciliarDuenoDeBorradores`, después
 * del bloque de purga, así que la primerísima ejecución en cada teléfono --el
 * `INITIAL_SESSION` del arranque siguiente al despliegue-- no encontraba marca,
 * entraba a purgar, y borraba del directorio de documentos los borradores de
 * TODO el mundo: las diecisiete fotos del asistente de publicación con la
 * tarjeta de circulación y la póliza, y las dieciséis de una entrega a medias.
 * El cambio hecho para no perder trabajo destruía, una sola vez y a todos,
 * exactamente el trabajo que decía proteger.
 *
 * POR QUÉ ADOPTAR ES DEMOSTRABLEMENTE SEGURO, y no una concesión: hasta el
 * commit que introdujo todo esto, CUALQUIER `SIGNED_OUT` purgaba borradores y
 * bytes. Entonces un borrador que sigue en disco en el momento en que esta
 * función existe por primera vez solo puede pertenecer a la sesión que sigue
 * abierta — o sea, a quien está entrando. No hay un tercer caso.
 *
 * A partir de la primera marca, la regla vuelve a ser estricta: marca distinta,
 * se purga. Que es el caso que este archivo existe para cubrir.
 */
export function hayQuePurgarBorradoresDe(userId: string | null | undefined): boolean {
  if (!userId) return false
  const dueno = duenoDeBorradores()
  // `null` es "todavía nadie reclamó esto", no "es de un desconocido".
  if (dueno === null) return false
  return dueno !== userId
}
